import { normalizeCountryCode } from "./clubAdminOnboarding";
import { isLuxembourgClubCountry } from "../constants/countries";

const FLEMISH_MARKERS = [
  "vttl",
  "vttl.be",
  "antwerpen",
  "oost-vlaanderen",
  "west-vlaanderen",
  "vlaams-brabant",
  "vlaams-brabant & br.",
  "limburg",
  "vlaanderen",
  "flanders",
  "flandres",
  "flandre",
  "vlaams",
  "sport vlaanderen",
];

const WALLOON_MARKERS = [
  "aftt",
  "aftt.be",
  "hainaut",
  "liège",
  "liege",
  "namur",
  "brabant wallon",
  "br. & brabant wallon",
  "wallonie",
  "wallonia",
  "walloon",
];

const VTTL_CODE_PREFIXES = ["ovl", "wvl", "vlb", "lk", "a"];
const AFTT_CODE_PREFIXES = ["bbw", "lx", "h", "l", "n"];

const COUNTRY_TO_APP_LANG = {
  LU: "fr",
  FR: "fr",
  MC: "fr",
  BE: "fr",
  NL: "nl",
  DE: "de",
  AT: "de",
  CH: "de",
  LI: "de",
  ES: "es",
  MX: "es",
  AR: "es",
  CL: "es",
  CO: "es",
  PE: "es",
  UY: "es",
  PY: "es",
  EC: "es",
  BO: "es",
  VE: "es",
  CU: "es",
  DO: "es",
  GT: "es",
  HN: "es",
  SV: "es",
  NI: "es",
  CR: "es",
  PA: "es",
  PR: "es",
  PT: "pt",
  BR: "pt",
  GF: "fr",
  GY: "en",
  SR: "nl",
  GB: "en",
  UK: "en",
  IE: "en",
  US: "en",
  CA: "en",
  AU: "en",
  NZ: "en",
};

const APP_LANGS = ["en", "fr", "de", "es", "nl", "pt"];

function haystack(club) {
  if (!club) return "";
  return [
    club.club_description,
    club.club_code,
    club.club_id,
    club.club_name,
    club.city,
    club.club_address,
    club.country,
    club.club_country,
    club.club_website,
    club.federation,
    club.province,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function postalLang(postalCode) {
  const n = parseInt(String(postalCode || "").replace(/\D/g, "").slice(0, 4), 10);
  if (!Number.isFinite(n) || n < 1000 || n > 9999) return null;
  if ((n >= 1500 && n <= 1999) || (n >= 2000 && n <= 3999) || (n >= 8000 && n <= 9999)) {
    return "nl";
  }
  if ((n >= 1300 && n <= 1499) || (n >= 4000 && n <= 7999)) {
    return "fr";
  }
  return null;
}

function clubPostalCode(club) {
  if (!club) return null;
  const direct = String(club.postal_code || "").replace(/\D/g, "").slice(0, 4);
  if (direct.length === 4) return direct;
  const blob = [club.club_address, club.city].filter(Boolean).join(" ");
  const match = blob.match(/\b([1-9]\d{3})\b/);
  return match ? match[1] : null;
}

function federationLangFromCode(club) {
  if (!club) return null;
  const candidates = [club.club_code, club.club_id, club.code]
    .filter(Boolean)
    .map((raw) =>
      String(raw)
        .trim()
        .replace(/^club_be_/i, "")
        .replace(/^club_/i, "")
    );
  for (const code of candidates) {
    if (VTTL_CODE_PREFIXES.some((p) => new RegExp(`^${p}\\d`, "i").test(code))) {
      return "nl";
    }
    if (AFTT_CODE_PREFIXES.some((p) => new RegExp(`^${p}\\d`, "i").test(code))) {
      return "fr";
    }
  }
  return null;
}

/** VTTL / distinctive AFTT codes — do not use short H/L/N (collide with other countries). */
const DISTINCTIVE_BE_CODE = /^(ovl|wvl|vlb|lk|bbw|lx)\d/i;
const VTTL_ANTWERP_CODE = /^a\d{3,}/i;

/**
 * True when this is a Belgian club even if tbl_club.country is still LU.
 * FLTT classement wording must not use country=LU alone.
 */
export function isBelgiumClub(club) {
  if (!club) return false;
  const country = normalizeCountryCode(
    club.country || club.club_country || club.clubCountryCode || club.clubCountry
  );
  if (country === "BE") return true;
  if (/^club_be_/i.test(String(club.club_id || ""))) return true;
  const text = haystack(club);
  if (/\bbelgium\b|\bbelgique\b|\bbelgi[eë]\b|\bvttl\b|\baftt\b/.test(text)) return true;
  const code = String(club.club_code || "")
    .trim()
    .replace(/^club_be_/i, "")
    .replace(/^club_/i, "");
  if (DISTINCTIVE_BE_CODE.test(code) || VTTL_ANTWERP_CODE.test(code)) return true;
  return false;
}

/**
 * True only for a Luxembourg club (FLTT). Belgian clubs stored as country=LU stay false.
 * Unknown / missing club → false so UI does not mention FLTT by default.
 */
export function isLuxembourgFederationClub(club) {
  if (!club) return false;
  if (isBelgiumClub(club)) return false;
  if (/^club_lu_/i.test(String(club.club_id || ""))) return true;
  return isLuxembourgClubCountry(
    club.country || club.club_country || club.clubCountryCode || club.clubCountry
  );
}

/** True for FFTT clubs (France), including ids from load_france_data (`club_fr_<number>`). */
export function isFranceFederationClub(club) {
  if (!club) return false;
  if (/^club_fr_/i.test(String(club.club_id || ""))) return true;
  const country = normalizeCountryCode(
    club.country || club.club_country || club.clubCountryCode || club.clubCountry
  );
  return country === "FR";
}

/** True for DTTB clubs (Germany), including ids from load_germany_data (`club_de_*`). */
export function isGermanyClub(club) {
  if (!club) return false;
  if (/^club_de_/i.test(String(club.club_id || ""))) return true;
  const country = normalizeCountryCode(
    club.country || club.club_country || club.clubCountryCode || club.clubCountry
  );
  if (country === "DE") return true;
  const text = haystack(club);
  return /\bgermany\b|\bdeutschland\b|\bdttb\b/.test(text);
}

/**
 * Club used for federation wording: club record wins, never the player's passport country.
 */
export function federationClubFromUser(user, brand) {
  const details = user?.club_details || user?.club || {};
  return {
    ...details,
    country: details.country || user?.club_country || brand?.country || null,
    club_country: details.club_country || user?.club_country || details.country || null,
    club_id: details.club_id || user?.club_id || brand?.club_id || null,
    club_code: details.club_code || user?.club_code || brand?.club_code || null,
    club_name: details.club_name || user?.club_name || brand?.brand_name || null,
    club_address: details.club_address || details.address || user?.club_address || null,
  };
}

function belgiumClubLanguage(club) {
  const text = haystack(club);
  if (FLEMISH_MARKERS.some((p) => text.includes(p))) return "nl";
  if (WALLOON_MARKERS.some((p) => text.includes(p))) return "fr";
  const fromCode = federationLangFromCode(club);
  if (fromCode) return fromCode;
  const fromPostal = postalLang(clubPostalCode(club));
  if (fromPostal) return fromPostal;
  if (/(^|[^a-z])luxembourg([^a-z]|$)/.test(text)) return "fr";
  return "fr";
}

export function clubCountryLanguage(club) {
  if (!club) return null;
  const country = normalizeCountryCode(
    club.country || club.club_country || club.clubCountryCode || club.clubCountry
  );
  if (isBelgiumClub(club)) return belgiumClubLanguage(club);
  const mapped = COUNTRY_TO_APP_LANG[country];
  return APP_LANGS.includes(mapped) ? mapped : "en";
}

export function clubLanguageFromStoredUser(user) {
  if (!user) return null;
  const club = user.club_details || user.club || {};
  return clubCountryLanguage({
    ...club,
    country: club.country || user.club_country || user.country,
    club_country: club.club_country || user.club_country,
  });
}

export function applyClubAdminUiLanguage(i18n, userOrClub) {
  const fromClub =
    userOrClub && (userOrClub.club_id || userOrClub.club_code || userOrClub.country)
      ? clubCountryLanguage(userOrClub)
      : null;
  const lang = String(userOrClub?.user_lang || fromClub || clubLanguageFromStoredUser(userOrClub) || "")
    .substring(0, 2)
    .toLowerCase();
  if (!APP_LANGS.includes(lang)) return lang || null;
  const current = String(i18n?.language || "")
    .substring(0, 2)
    .toLowerCase();
  try {
    localStorage.setItem("lng", lang);
    localStorage.setItem("i18nextLng", lang);
    // Re-emitting languageChanged remounts the invite loader and blocks the UI.
    if (current !== lang) i18n?.changeLanguage?.(lang);
  } catch {
    /* ignore */
  }
  return lang;
}

export const DIGIPING_APP_LANGS = APP_LANGS;

export const INVITE_UI_LANG_KEY = "invite_ui_lang";

export function markInviteUiLangOverride(code) {
  const lang = String(code || "")
    .substring(0, 2)
    .toLowerCase();
  if (!APP_LANGS.includes(lang)) return null;
  try {
    sessionStorage.setItem(INVITE_UI_LANG_KEY, lang);
  } catch {
    /* ignore */
  }
  return lang;
}

export function inviteUiLangOverride() {
  try {
    const stored = String(sessionStorage.getItem(INVITE_UI_LANG_KEY) || "")
      .substring(0, 2)
      .toLowerCase();
    return APP_LANGS.includes(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** Club/region language for invite landing, unless the user already picked a language. */
export function applyInvitePageLanguage(i18n, club) {
  const override = inviteUiLangOverride();
  if (override) {
    return applyClubAdminUiLanguage(i18n, { user_lang: override });
  }
  return applyClubAdminUiLanguage(i18n, club);
}
